import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PredefinedPartLabel } from '../../../../types';
import { parsePartLabelsJson } from '../../../../utils/cadPartLabels';

type LinkCad = (
  cadModelId: string,
  predefinedPartLabels?: PredefinedPartLabel[],
) => Promise<void>;

/**
 * Owns the CAD-link fallback UI state: the selected model, the part-label JSON
 * buffer, its parse error, and the 3D part-label editor dialog. All handlers are
 * lifted verbatim from DRBFMResultsStep so behaviour is unchanged.
 */
export function useCadLinking(onLinkCad?: LinkCad) {
  const { t } = useTranslation();
  const [selectedCadModelId, setSelectedCadModelId] = useState<string | undefined>();
  const [partLabelsJson, setPartLabelsJson] = useState('');
  const [partLabelsError, setPartLabelsError] = useState<string | null>(null);
  const [partLabelEditorOpen, setPartLabelEditorOpen] = useState(false);
  const [editorInitialLabels, setEditorInitialLabels] = useState<PredefinedPartLabel[]>([]);

  const handleLinkCad = async () => {
    if (!selectedCadModelId || !onLinkCad) return;
    try {
      const labels = parsePartLabelsJson(partLabelsJson, t);
      setPartLabelsError(null);
      await onLinkCad(selectedCadModelId, labels.length > 0 ? labels : undefined);
    } catch (err) {
      setPartLabelsError(err instanceof Error ? err.message : t('drbfm.cadLink.parseError', '部位ラベルJSONを読み取れません'));
    }
  };

  const handleCadModelChange = (cadModelId: string | undefined) => {
    if (cadModelId !== selectedCadModelId) {
      // Part-label coordinates are model-specific. Never carry them silently
      // to another CAD model where they could create plausible but wrong pins.
      setPartLabelsJson('');
      setEditorInitialLabels([]);
      setPartLabelsError(null);
    }
    setSelectedCadModelId(cadModelId);
  };

  const handleOpenPartLabelEditor = () => {
    try {
      setEditorInitialLabels(parsePartLabelsJson(partLabelsJson, t));
      setPartLabelsError(null);
      setPartLabelEditorOpen(true);
    } catch (err) {
      setPartLabelsError(
        err instanceof Error
          ? err.message
          : t('drbfm.cadLink.parseError', '部位ラベルJSONを読み取れません'),
      );
    }
  };

  const handleApplyPartLabels = (labels: PredefinedPartLabel[]) => {
    setPartLabelsJson(JSON.stringify({ parts: labels }, null, 2));
    setPartLabelsError(null);
  };

  const handlePartLabelsJsonChange = (value: string) => {
    setPartLabelsJson(value);
    if (partLabelsError) setPartLabelsError(null);
  };

  const closePartLabelEditor = () => setPartLabelEditorOpen(false);

  return {
    selectedCadModelId,
    partLabelsJson,
    partLabelsError,
    partLabelEditorOpen,
    editorInitialLabels,
    handleLinkCad,
    handleCadModelChange,
    handleOpenPartLabelEditor,
    handleApplyPartLabels,
    handlePartLabelsJsonChange,
    closePartLabelEditor,
  };
}
